/**
 * L'entrée de la boutique : qui nous sommes, comment ça marche, et le pain.
 *
 * Demande de Philippe (27/09/2026), sur le modèle de The Barn : une photo de la
 * ferme à l'ouverture du site et une courte explication. La boutique s'ouvrait
 * directement sur 1 000 vignettes, sans dire qui vend ni comment on commande —
 * et la précommande du pain « pose beaucoup de questions aux clients » : sa
 * ligne du temps est ici, dessinée depuis les réglages du boulanger.
 *
 * Affichée SEULEMENT à l'entrée du catalogue (ni recherche, ni rayon, ni
 * filtre), elle défile avec la page. Un client qui revient chaque semaine peut
 * la masquer (30 jours, sur cet appareil) — et la rouvrir par le lien qui la
 * remplace (`reafficherAccueil`). Aucune heure écrite en dur : le
 * bandeau d'état au-dessus dit déjà quand le frais rouvre.
 */
import { useEffect, useState } from 'react';
import HowItWorksModal from './HowItWorksModal';
import LigneDuTempsPain from './LigneDuTempsPain';
import FenetreFrais from './FenetreFrais';
import HorairesMagasin from './HorairesMagasin';
import { fetchShopConfigCached } from '../../hooks/ecommerce/useShopOrdering';

const CLE = 'folies_accueil_masque_jusqua';
const TRENTE_JOURS = 30 * 86_400_000;

export function accueilMasque(): boolean {
  try {
    const jusqua = Number(localStorage.getItem(CLE));
    return Number.isFinite(jusqua) && jusqua > Date.now();
  } catch {
    return false;
  }
}

/**
 * Rouvrir l'entrée masquée. « Masquer » cachait le bloc 30 jours SANS chemin de
 * retour : Philippe l'a masqué le 07/10/2026 et ne pouvait plus le revoir.
 * La boutique affiche désormais, à sa place, un lien qui appelle ceci.
 */
export function reafficherAccueil(): void {
  try { localStorage.removeItem(CLE); } catch { /* rien à effacer */ }
}

export default function AccueilBoutique({
  onVoirProduits,
  onMasquer,
  rayonsFenetre,
  rayonsToujours,
}: {
  onVoirProduits: () => void;
  onMasquer: () => void;
  /** Noms des rayons qui suivent la fenêtre de commande / commandables tous les jours. */
  rayonsFenetre?: string[];
  rayonsToujours?: string[];
}) {
  const [aide, setAide] = useState(false);
  const [adresse, setAdresse] = useState<string | null>(null);

  useEffect(() => {
    let annule = false;
    fetchShopConfigCached()
      .then((c) => { if (!annule) setAdresse(c.store?.address ?? null); })
      .catch(() => {});
    return () => { annule = true; };
  }, []);

  const masquer = () => {
    try { localStorage.setItem(CLE, String(Date.now() + TRENTE_JOURS)); } catch { /* navigation privée : masqué pour cette visite */ }
    onMasquer();
  };

  const points = [
    { titre: 'Nos légumes et une épicerie 100 % bio', texte: 'Ce que nous cultivons à Elouges, et le reste choisi avec l’exigence d’un producteur.' },
    { titre: 'Commandez et payez en ligne', texte: 'Par carte ou avec vos FOLIES. Rien à payer au retrait.' },
    { titre: 'Retirez au magasin, le jour choisi', texte: `${adresse ? adresse + ' — ' : ''}vous venez quand vous recevez le message « commande prête ».` },
  ];

  return (
    <section className="shop-sans mb-5" aria-labelledby="titre-accueil">
      <div className="relative overflow-hidden rounded-2xl bg-white border border-black/[.06] md:grid md:grid-cols-2">
        {/* Téléphone : une BANDE recadrée (960×540, 49-100 Ko) au lieu de la photo
            carrée (960×920, 139 Ko) dont on ne montrait qu'une tranche — elle
            arrivait après le catalogue et restait pixelisée (capture du 28/09).
            Chargée en priorité : c'est la première chose qu'on voit.
            JPEG « baseline », pas progressif : sur iPad/iPhone, Safari restait
            bloqué sur la première passe d'un JPEG progressif (gros pavés, bas de
            l'image vide — captures du 28/09). Photo du 29/09 (2 048 px), fichiers « -v3 » : le serveur sert
            les images en cache « immutable » un an, toujours renommer. */}
        <picture className="block md:h-full">
          <source
            media="(min-width: 768px)"
            srcSet="/accueil/ferme-elouges-v3-960.jpg 960w, /accueil/ferme-elouges-v3-1600.jpg 1600w"
            sizes="50vw"
          />
          <img
            src="/accueil/ferme-elouges-bande-v3-800.jpg"
            srcSet="/accueil/ferme-elouges-bande-v3-800.jpg 800w, /accueil/ferme-elouges-bande-v3-1280.jpg 1280w"
            sizes="100vw"
            alt="Salades au premier plan, tunnels et église d'Elouges au fond, dans la lumière du soir"
            className="w-full aspect-[16/9] max-h-56 sm:max-h-72 md:max-h-none md:aspect-auto md:h-full object-cover bg-[#dfe3d6]"
            style={{ objectPosition: '50% 45%' }}
            width={1280}
            height={720}
            loading="eager"
            {...({ fetchpriority: 'high' } as any)}
          />
        </picture>
        <div className="p-5 sm:p-6 flex flex-col justify-center">
          <button
            type="button"
            onClick={masquer}
            className="absolute top-2 right-2 rounded-full bg-white/85 px-3 py-1.5 text-[12px] text-[#6b6550] shadow-sm active:bg-white"
          >
            Masquer
          </button>
          <p className="text-[12px] font-bold uppercase tracking-[.08em] text-[#4a5c3a]">Les Folies Maraîchères · Elouges</p>
          <h2 id="titre-accueil" className="shop-serif mt-1 text-[26px] sm:text-[30px] leading-[1.15] font-bold text-[#3a3324]" style={{ textWrap: 'balance' } as any}>
            Maraîchers bio à Elouges, à deux pas de chez vous
          </h2>
          {/* Ce qui nous distingue des autres boutiques bio en ligne (Philippe, 28/09/2026) :
              pas de frais de préparation, le prix du magasin et rien d'autre. */}
          <div className="mt-4 flex items-center gap-3 rounded-xl border-2 border-[#b45f2e]/35 bg-[#b45f2e]/[.06] px-4 py-3">
            <span className="shop-serif shrink-0 text-[30px] font-bold leading-none text-[#b45f2e]" aria-hidden>0&nbsp;€</span>
            <span className="text-[14.5px] leading-snug text-[#4a4530]">
              <strong className="block text-[15.5px] text-[#3a3324]">Aucun frais de préparation.</strong>
              Vous payez le prix juste, celui du magasin. Point.
            </span>
          </div>
          <ul className="mt-4 space-y-3">
            {points.map((p) => (
              <li key={p.titre} className="flex gap-3">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#4a5c3a] text-[13px] font-bold text-white" aria-hidden>✓</span>
                <span className="text-[14.5px] leading-snug text-[#4a4530]">
                  <strong className="text-[#3a3324]">{p.titre}.</strong> {p.texte}
                </span>
              </li>
            ))}
          </ul>
          <div className="mt-5 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onVoirProduits}
              className="min-h-[48px] rounded-xl bg-[#b45f2e] px-5 text-[15px] font-bold text-white active:bg-[#9c5127]"
            >
              Voir les produits
            </button>
            <button
              type="button"
              onClick={() => setAide(true)}
              className="min-h-[48px] rounded-xl border-2 border-[#4a5c3a]/40 px-5 text-[15px] font-semibold text-[#4a5c3a] active:bg-[#eef2e8]"
            >
              Comment ça marche ?
            </button>
          </div>
        </div>
      </div>

      <div className="mt-3">
        <HorairesMagasin />
      </div>

      <div className="mt-3">
        <FenetreFrais rayonsFenetre={rayonsFenetre} rayonsToujours={rayonsToujours} />
      </div>

      <div className="mt-3">
        <LigneDuTempsPain />
      </div>

      <HowItWorksModal open={aide} onClose={() => setAide(false)} />
    </section>
  );
}
